import React, { useCallback, useMemo } from 'react';
import { Button, Dropdown, Menu, Spin } from 'antd';
import { LeftOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';
import moment from 'moment';

interface CustomCalendarToolbarProps {
	date: Date;
	view: string;
	views: string[];
	label: string;
	onNavigate: (action: string) => void;
	onView: (view: string) => void;
}

const calendarStatusLegendConfig = [
	{
		label: 'Scheduled',
		style: {
			borderWidth: '1px',
			borderStyle: 'dashed',
		},
		indicatorClassName: 'border-color-grey-5 bg-white',
	},
	{
		label: 'Scheduled (Past Due)',
		style: {
			borderWidth: '1px',
			borderStyle: 'solid',
		},
		indicatorClassName: 'bg-[#FFE4E5] border-color-grey-5',
	},
	{
		label: 'Checked In',
		style: {
			borderWidth: '1px',
			borderStyle: 'solid',
		},
		indicatorClassName: 'bg-[#D2E2E6] border-color-grey-5',
	},
	{
		label: 'On Hold/Incomplete',
		style: {
			borderWidth: '1px',
			borderStyle: 'solid',
		},
		indicatorClassName: 'bg-[#FEEFD0] border-color-grey-5',
	},
	{
		label: 'Completed',
		style: {
			borderWidth: '1px',
			borderStyle: 'solid',
		},
		indicatorClassName: 'bg-grey-2 border-color-grey-2',
	},
];

const CustomCalendarToolbar: React.FC<CustomCalendarToolbarProps> = ({
	date,
	view,
	views,
	label,
	onNavigate,
	onView,
	fetching,
}) => {
	const handleToday = useCallback(() => {
		onNavigate('TODAY');
	}, [onNavigate]);

	const handlePrev = useCallback(() => {
		onNavigate('PREV');
	}, [onNavigate]);

	const handleNext = useCallback(() => {
		onNavigate('NEXT');
	}, [onNavigate]);

	const handleViewChange = useCallback(
		({ key }: { key: string }) => {
			onView(key as string);
		},
		[onView]
	);

	// Format label to show month and year (e.g., "October 2025")
	const currentViewLabel = useMemo(
		() =>
			view === 'week' ? moment(date).format('MMMM YYYY') : moment(date).format('ddd, DD MMM YYYY'),
		[view, date]
	);

	const viewMenu = useMemo(
		() => (
			<Menu onClick={handleViewChange}>
				{views.map((viewName) => (
					<Menu.Item key={viewName}>
						{viewName.charAt(0).toUpperCase() + viewName.slice(1)}
					</Menu.Item>
				))}
			</Menu>
		),
		[views, handleViewChange]
	);

	return (
		<div className="mb-4 flex flex-wrap items-center justify-between gap-y-2 px-1 py-4">
			<div className="flex items-center gap-4">
				<div className="h5medium text-character-primary">{currentViewLabel} </div>
				{calendarStatusLegendConfig.map((item) => (
					<div className="flex items-center gap-2" key={item.label}>
						<div
							className={`h-3 w-3 rounded-full ${item.indicatorClassName || ''}`}
							style={item.style}
						></div>
						<span className="footnoteregular text-character-secondary">{item.label}</span>
					</div>
				))}
			</div>
			<div className="flex items-center gap-2">
				<Dropdown overlay={viewMenu} trigger={['click']}>
					<Button className="flex items-center gap-1">
						{view.charAt(0).toUpperCase() + view.slice(1)}
						<DownOutlined className="text-xs" />
					</Button>
				</Dropdown>
				<Button onClick={handleToday}>Today</Button>
				<Button icon={<LeftOutlined />} onClick={handlePrev} />
				<Button icon={<RightOutlined />} onClick={handleNext} />
			</div>
		</div>
	);
};

export default CustomCalendarToolbar;
